import './index.scss';
import InfiniteLoader from 'Components/Elements/InfiniteLoader';
import { useNavHideMenuById } from 'core/hooks/navIsHidden';
import { Tag, actions, getTagLocale } from 'core/redux/features/blogs';
import { AppState } from 'core/redux/reducers';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useTrans } from '../../../core/hooks';
import { selectLocal } from '../../../core/redux/features/products/reducers/productTypes';
import { Item } from './Item';

interface TagTree extends Tag {
  children: TagTree[];
}

const tagTree = function tagTree(tag: Tag, tags: Tag[]): TagTree {
  const activeTag: TagTree = {
    ...tag,
    children: [],
  };

  tags.forEach(item => {
    if (item.parent_id === activeTag.id) {
      activeTag.children.push(tagTree(item, tags));
    }
  });

  return activeTag;
};

const allTagTree = (tags: Tag[]): TagTree[] => {
  return tags
    .filter(i => !i.parent_id)
    .map(item => {
      return tagTree(
        item,
        tags.filter(i => i.parent_id > 0),
      );
    });
};

// const groupByParentId = (tags: Tag[]) => {
//   const results: Record<number, Tag[]> = {};
//
//   tags.forEach(item => {
//     const parentId = item.parent_id ?? 0;
//     if (!results[parentId]) {
//       results[parentId] = [];
//     }
//
//     results[parentId].push(item);
//   });
//
//   return results;
// };

const selectTagTree = createSelector(
  [(state: AppState) => state.blogs?.extra?.tags ?? []],
  tags => {
    return allTagTree(tags);
  },
);

// const groupByParent = createSelector(
//   [(state: AppState) => state.blogs?.extra?.tags ?? []],
//   tags => {
//     return groupByParentId(tags);
//   },
// );

const toggleTag = (tags: null | TagTree[], tag: TagTree) => {
  if (tags === null) {
    return [tag];
  }
  //
  // const tagIndex = tags.findIndex(item => item.id === tag.id);
  // const isRemove = tagIndex > -1;
  //
  // if (isRemove) {
  //   const newTags = [];
  //   tags.forEach((item, index) => {
  //     if (index < tagIndex) {
  //       newTags.push(item);
  //     }
  //   });
  //   return newTags;
  // }

  const childToRemove = [];
  const newTags = tags
    .filter(item => {
      const shouldRemove = item.parent_id !== tag.parent_id;
      if (item.parent_id === tag.parent_id) {
        childToRemove.push(...getAllChildren(item).map(i => i.id));
      }
      return shouldRemove;
    })
    .filter(item => childToRemove.indexOf(item.id) === -1);

  return [...newTags, tag];
};

const getAllChildren = function getAllChildren(tag: TagTree) {
  if (tag.children.length === 0) {
    return [];
  }

  let results = [];
  tag.children.forEach(item => {
    results.push(item);
    results.push(...getAllChildren(item));
  });

  return results;
};

export default function BlogList() {
  const dispatch = useDispatch();
  const deviceType = useSelector((state: AppState) => state.deviceType);
  const { loading, next_page, data } = useSelector(
    (state: AppState) => state.blogs.results.all,
  );
  const { tagTree, locale } = useSelector((state: AppState) => ({
    // tags: state.blogs?.extra?.tags ?? [],
    // groupTags: groupByParent(state),
    tagTree: selectTagTree(state),
    locale: state.local,
  }));

  const [activeTags, setActiveTags] = useState<TagTree[] | null>(null);

  useEffect(() => {
    let tags = [];
    if (activeTags && activeTags?.length > 0) {
      tags = activeTags?.map(o => o.id);

      // when the parent tag is selected
      // we should also show all the blog for its child
      const lastTag = activeTags[activeTags.length - 1];
      tags.push(...getAllChildren(lastTag).map(o => o.id));
    }

    dispatch(
      actions.updateParamsAndReset({
        tags,
      }),
    );
  }, [dispatch, activeTags]);

  const loadNextPage = () => {
    dispatch(actions.nextPageRequest());
  };

  useNavHideMenuById('backbtn', deviceType !== 'mobile');

  const local = useSelector(selectLocal);
  const path = local !== 'en' ? `/` : `/${local}/`;

  function renderTags(tags: TagTree[], _isParent: boolean = false) {
    return (
      <>
        {tags?.map(item => {
          const tag = getTagLocale(item, locale);
          return (
            <a
              key={tag.id}
              className={'tag-list__item'}
              onClick={e => {
                e.preventDefault();
                setActiveTags(toggleTag(activeTags, item));

                // history.replace(`/guide?tag=${item.name}`);
              }}
              href={`/guide?tags=${item.id}`}
            >
              {tag.name}
            </a>
          );
        })}
      </>
    );
  }

  const renderChildTags = function renderChildTags(parentTag: TagTree) {
    return (
      <div key={parentTag.id} className={`tags-group`}>
        {renderTags(parentTag.children)}
      </div>
    );
  };

  const t = useTrans();

  return (
    <>
      <div className="tag-list">
        <a
          className="tag-list__item is-uppercase"
          onClick={e => {
            e.preventDefault();
            setActiveTags(null);
            // history.replace('/guide');
          }}
          href={'/guide?tag=all'}
        >
          {t('all')}
        </a>
        {renderTags(tagTree, true)}
        {activeTags?.map(renderChildTags)}
      </div>
      <InfiniteLoader
        className="columns is-multiline is-mobile is-variable is-2"
        hasNextPage={next_page !== null}
        loading={loading}
        items={data}
        loader={
          <>
            <Item deviceType={deviceType} />
            <Item deviceType={deviceType} />
            {deviceType !== 'mobile' ? <Item deviceType={deviceType} /> : null}
          </>
        }
        loadNextPage={loadNextPage}
      >
        {({ item }) => {
          let fromSlugs = item.slugs?.find(item => item.lang === locale);
          if (!fromSlugs) {
            return null;
          }

          return (
            <Item
              deviceType={deviceType}
              href={`${path}guide/${fromSlugs.slug}`}
              item={item}
            />
          );
        }}
      </InfiniteLoader>
    </>
  );
}
